import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, ArrowRight, Sparkles, MapPin, BookOpen, Loader2, Copy, X, CheckCircle, Clock, UserCheck, Search, Users } from 'lucide-react';
import { usePlaybookSocket } from '../../hooks/usePlaybookSocket';
import DiscReviewModal from './DiscReviewModal';
import { PracticeModal } from './PracticeModal';
import { PreCallSmsSendButtons, canSendSms } from '../../pages/call-detail/CoachingPlaybookCard';
import { useAuth } from '../../contexts/AuthContext';
import CallRecordingButton from '../CallRecordingButton';
import { revivableTerm, TERM_CHIP_STYLE } from '../../lib/revivableTerms';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISC_COLOR = { D: '#EF4444', I: '#F59E0B', S: '#10B981', C: '#3B82F6' };

const _confidenceColor = (c) =>
  c >= 60 ? '#10B981' : c >= 35 ? '#F59E0B' : '#EF4444';

const TopAgentChip = ({ rec, onPick, rank }) => {
  const boost = Number(rec.chase_boost || 0);
  const chaseWins = Number(rec.chase_wins || 0);
  const chaseAttempts = Number(rec.chase_attempts || 0);
  const displayConf = Number(rec.effective_confidence ?? rec.confidence);
  return (
    <button
      type="button"
      onClick={() => onPick?.(rec)}
      className="w-full text-left px-2 py-1 rounded-md border border-slate-200 bg-white hover:bg-indigo-50 hover:border-indigo-300 transition-colors group"
      title={rec.reasoning}
      data-testid={`inline-chip-${rec.agent_id}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-white text-[9px] font-bold shrink-0"
              style={{ background: rank === 1 ? '#4F46E5' : rank === 2 ? '#7C3AED' : '#94A3B8' }}>
          {rank}
        </span>
        <span className="text-[11px] font-semibold text-slate-800 truncate flex-1">
          {rec.agent_name?.split(' · ')[0] || '—'}
        </span>
        {boost > 0 && (
          <span
            className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-[1px] rounded bg-emerald-100 text-emerald-800 shrink-0"
            title={`Proven chase performer: ${chaseWins}/${chaseAttempts} follow-ups closed. Ranking boosted by +${boost}.`}
            data-testid={`inline-chip-boost-${rec.agent_id}`}
          >
            +{boost}
          </span>
        )}
        <span className="text-[10px] font-bold tabular-nums text-slate-600 shrink-0">
          {displayConf}
        </span>
      </div>
      <div className="mt-0.5 h-1 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full"
             style={{ width: `${Math.min(100, displayConf)}%`, background: _confidenceColor(displayConf) }} />
      </div>
    </button>
  );
};

const _fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  } catch {
    return iso.slice(0, 10);
  }
};

const _ageLabel = (days) => {
  if (days == null) return null;
  if (days === 0) return 'today';
  if (days === 1) return '1d old';
  if (days < 7) return `${days}d old`;
  if (days < 30) return `${Math.round(days / 7)}w old`;
  if (days < 365) return `${Math.round(days / 30)}mo old`;
  return `${Math.round(days / 365)}y old`;
};

const _ageColor = (days) => {
  if (days == null) return 'text-slate-400';
  if (days <= 3) return 'text-emerald-600';
  if (days <= 14) return 'text-amber-600';
  return 'text-rose-600';
};

const AgentPill = ({ name, mode = 'original', site, byManager }) => {
  const clr = mode === 'suggested' ? '#10B981' : '#94A3B8';
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-xs font-semibold text-slate-800">{name || '—'}</span>
      {site && (
        <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-500">
          <MapPin className="w-2.5 h-2.5" style={{ color: clr }} /> {site}
        </span>
      )}
      {byManager && (
        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-teal-700">
          <UserCheck className="w-2.5 h-2.5" /> Manager re-allocation
        </span>
      )}
    </div>
  );
};

/**
 * Revivable Deal Queue — recoverable deferrals with an auto-assigned same-site
 * follow-up owner (derived from the Agent × DISC compatibility matrix).
 * Managers can override the assignment via a quick inline select.
 */
const RevivableDealQueue = ({ queue = [], domainId, onRefresh, agentMode = false, termDefault = 'Opp' }) => {
  const [statuses, setStatuses] = useState({});   // call_id → local status
  const [assignments, setAssignments] = useState({}); // call_id → {agent_id, agent_name}
  const [playbookFor, setPlaybookFor] = useState(null); // call row currently open in playbook modal
  const [reassignFor, setReassignFor] = useState(null); // call row currently open in reassign modal
  const [discReviewFor, setDiscReviewFor] = useState(null); // call_id currently open in DISC review modal
  const [recommendFor, setRecommendFor] = useState(null); // call row currently open in recommend-agents modal
  const [sortBy, setSortBy] = useState('confidence'); // 'confidence' | 'call_date' | 'graded_date' | 'latest_call'

  const changeSort = (next) => {
    setSortBy(next);
    onRefresh?.(next);
  };

  const updateStatus = async (callId, next) => {
    setStatuses((s) => ({ ...s, [callId]: next }));
    try {
      await axios.patch(`${API}/calls/${callId}`, { revivable_status: next });
      toast.success(`Marked ${next}`);
    } catch {
      toast.error('Could not save status');
      setStatuses((s) => ({ ...s, [callId]: undefined }));
    }
  };

  if (!queue.length) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-bold text-slate-900">Revivable Opportunity Queue</h3>
        <div className="text-center text-slate-400 italic py-8 border border-dashed border-slate-200 rounded-xl">
          No revivable opportunities detected in the current window.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="revivable-queue">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-900 inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-amber-600" />
            Revivable Opportunity Queue
          </h3>
          <p className="text-xs text-slate-500">
            Deferred but recoverable — auto-assigned to the best-matched rep on the same site.
          </p>
        </div>
        {onRefresh && (
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider" htmlFor="revivable-sort">
              Sort by
            </label>
            <select
              id="revivable-sort"
              value={sortBy}
              onChange={(e) => changeSort(e.target.value)}
              className="text-[11px] font-semibold px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
              data-testid="revivable-sort-select"
            >
              <option value="confidence">Highest confidence</option>
              <option value="latest_call">Latest call (freshest first)</option>
              <option value="call_date">Oldest call (opps at risk)</option>
              <option value="graded_date">Oldest grading</option>
            </select>
            <button
              type="button"
              onClick={() => onRefresh(sortBy)}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 inline-flex items-center gap-1.5"
              data-testid="revivable-refresh-btn"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Caller</th>
              <th className="text-left px-3 py-2 font-semibold">Date</th>
              <th className="text-center px-2 py-2 font-semibold">DISC</th>
              <th className="text-right px-2 py-2 font-semibold">Confidence</th>
              <th className="text-left px-3 py-2 font-semibold min-w-[220px]">Reason</th>
              <th className="text-left px-3 py-2 font-semibold min-w-[220px]">Recommended Follow-up</th>
              <th className="text-left px-3 py-2 font-semibold">Original</th>
              <th className="text-left px-3 py-2 font-semibold min-w-[220px]">
                <span className="inline-flex items-center gap-1 text-indigo-700">
                  <Sparkles className="w-3 h-3" /> {agentMode ? 'Best-fit' : 'Top-3 best-fit (site-scoped)'}
                </span>
              </th>
              <th className="text-center px-2 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {queue.map((row) => {
              const disc = row.caller_disc || null;
              const st = statuses[row.call_id] || row.status || 'pending';
              return (
                <tr key={row.call_id} className="border-t border-slate-100 hover:bg-slate-50 align-top">
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-700">
                    <div className="flex items-center gap-1.5">
                      <span>{row.customer_phone || '—'}</span>
                      <CallRecordingButton callId={row.call_id} size="xs" />
                    </div>
                    <span
                      className={`inline-block mt-1 text-[9px] font-bold uppercase tracking-wide border rounded px-1 py-0.5 ${TERM_CHIP_STYLE[revivableTerm(row.site, termDefault)]}`}
                      data-testid={`term-chip-${row.call_id}`}
                    >{revivableTerm(row.site, termDefault)}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500 tabular-nums">
                    <div className="flex flex-col gap-0.5">
                      <span>{_fmtDate(row.created_at)}</span>
                      {row.age_days != null && (
                        <span
                          className={`text-[10px] font-semibold ${_ageColor(row.age_days)}`}
                          data-testid={`age-badge-${row.call_id}`}
                        >
                          {_ageLabel(row.age_days)}
                        </span>
                      )}
                      {row.is_backfilled && (
                        <span
                          className="inline-flex items-center gap-1 text-[9px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded px-1 py-0.5 mt-0.5 w-fit"
                          title={`Graded ${_fmtDate(row.graded_at)} — long after the call. Reasoning quotes may reflect older context.`}
                          data-testid={`backfilled-badge-${row.call_id}`}
                        >
                          Backfilled
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center">
                    {disc ? (
                      <button
                        type="button"
                        onClick={() => setDiscReviewFor(row.call_id)}
                        className="inline-block px-2 py-0.5 rounded text-[11px] font-bold text-white hover:opacity-80 transition-opacity"
                        style={{ background: DISC_COLOR[disc] || '#94A3B8' }}
                        data-testid={`disc-review-open-${row.call_id}`}
                        title="Click to review the DISC classification, see the evidence, or correct it"
                      >{disc}</button>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-xs text-slate-700 font-semibold">
                    {row.confidence_score ?? 0}%
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700">
                    {row.reason_detected || '—'}
                    {row.gymsales_matched && (
                      <span
                        className="inline-flex items-center gap-1 ml-1.5 text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5 align-middle"
                        title={`GymSales lead matched${row.gymsales_person_name ? `: ${row.gymsales_person_name}` : ''}${row.gymsales_matched_at ? ` · ${new Date(row.gymsales_matched_at).toLocaleString()}` : ''}`}
                        data-testid={`gymsales-matched-chip-${row.call_id}`}
                      >
                        GymSales{row.gymsales_person_name ? `: ${row.gymsales_person_name}` : ' matched'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700">
                    <div className="flex items-start gap-1">
                      <ArrowRight className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
                      {row.recommended_next_action || '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <AgentPill name={row.original_agent_name} site={row.site} mode="original" />
                  </td>
                  <td className="px-3 py-2 min-w-[220px]">
                    {agentMode ? (
                      // MC view — one best-fit name + score, view-only.
                      // Only supervisors can act on reallocation hints.
                      row.top_recommendations?.length ? (
                        <span className="text-xs text-slate-700" data-testid={`agent-best-fit-${row.call_id}`}>
                          <span className="font-semibold">{row.top_recommendations[0].agent_name}</span>
                          {row.top_recommendations[0].confidence != null && (
                            <span className="text-slate-400 ml-1 tabular-nums">{Math.round(row.top_recommendations[0].confidence)}%</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-[11px] text-slate-400 italic">—</span>
                      )
                    ) : (
                    <>
                    {row.strong_reassign && !row.reassigned_by_manager && !assignments[row.call_id] && (
                      <div
                        className="mb-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-800 bg-red-100 border border-red-200 rounded px-1.5 py-0.5"
                        title={row.strong_reassign_reason}
                        data-testid={`strong-reassign-badge-${row.call_id}`}
                      >
                        ⚠ Strong Reassign
                      </div>
                    )}
                    {row.reassigned_by_manager || assignments[row.call_id] ? (
                      <>
                        <AgentPill
                          name={assignments[row.call_id]?.agent_name || row.suggested_agent_name || row.original_agent_name}
                          site={row.site}
                          mode="suggested"
                          byManager
                        />
                        {row.manager_note && (
                          <p
                            className="mt-1 text-[10px] text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5 py-1 leading-snug"
                            data-testid={`manager-note-${row.call_id}`}
                            title={`Reassigned by ${row.manager_email || 'manager'}`}
                          >
                            <span className="font-semibold uppercase tracking-wider text-[9px] text-purple-500">Note</span>{' '}
                            {row.manager_note}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => setRecommendFor(row)}
                          className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 hover:text-indigo-900"
                          data-testid={`recommend-btn-${row.call_id}`}
                          title="See the top 3 site-scoped best-agent recommendations"
                        >
                          <Users className="w-2.5 h-2.5" /> Top 3 details
                        </button>
                      </>
                    ) : row.top_recommendations?.length ? (
                      <div className="space-y-1">
                        {row.top_recommendations.map((rec, i) => (
                          <TopAgentChip
                            key={rec.agent_id}
                            rec={rec}
                            rank={i + 1}
                            onPick={(picked) => setReassignFor({ ...row, __prefill_agent: picked })}
                          />
                        ))}
                        <button
                          type="button"
                          onClick={() => setRecommendFor(row)}
                          className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 hover:text-indigo-900"
                          data-testid={`recommend-btn-${row.call_id}`}
                          title="Open detailed reasoning + confidence"
                        >
                          <Users className="w-2.5 h-2.5" /> Details & reasoning
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-[11px] text-slate-500 italic leading-snug">
                          {row.caller_disc
                            ? 'No in-site match yet.'
                            : 'DISC profile pending — top-3 will appear once available.'}
                        </p>
                        <button
                          type="button"
                          onClick={() => setReassignFor(row)}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-700 hover:text-teal-900"
                          data-testid={`reassign-btn-${row.call_id}`}
                        >
                          <UserCheck className="w-2.5 h-2.5" /> Re-allocate manually
                        </button>
                      </div>
                    )}
                    </>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <div className="flex flex-col gap-1 items-stretch">
                      <button
                        onClick={() => setPlaybookFor(row)}
                        className="inline-flex items-center gap-1.5 justify-center text-[11px] font-bold px-3 py-2 rounded-lg bg-gradient-to-br from-teal-600 to-teal-700 hover:from-teal-500 hover:to-teal-600 text-white shadow-sm hover:shadow-md ring-1 ring-teal-500/20 transition-all"
                        data-testid={`playbook-btn-${row.call_id}`}
                        title="Open a callback playbook: opening line, checklist and points to cover"
                      >
                        <BookOpen className="w-3.5 h-3.5" /> Open Playbook
                      </button>
                      <select
                        className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-200 bg-white text-slate-700"
                        value={st}
                        onChange={(e) => updateStatus(row.call_id, e.target.value)}
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In progress</option>
                        <option value="follow_up_sent">Follow-up sent</option>
                        <option value="won">Won</option>
                        <option value="lost">Lost</option>
                      </select>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {playbookFor && (
        <PlaybookModal row={playbookFor} onClose={() => setPlaybookFor(null)} />
      )}

      {reassignFor && (
        <ReassignModal
          row={reassignFor}
          domainId={domainId}
          onClose={() => setReassignFor(null)}
          onDone={(nxt) => {
            setAssignments((a) => ({ ...a, [reassignFor.call_id]: { ...nxt, byManager: true } }));
            setReassignFor(null);
            toast.success(`Re-allocated to ${nxt.agent_name}`);
            onRefresh?.();
          }}
        />
      )}

      {discReviewFor && (
        <DiscReviewModal
          callId={discReviewFor}
          onClose={() => setDiscReviewFor(null)}
          onUpdated={() => onRefresh?.()}
        />
      )}

      {recommendFor && (
        <RecommendAgentsModal
          row={recommendFor}
          onClose={() => setRecommendFor(null)}
          onPick={(agent) => {
            setRecommendFor(null);
            // Open the reassign flow with the picked agent pre-noted for context
            setReassignFor({ ...recommendFor, __prefill_agent: agent });
          }}
        />
      )}
    </div>
  );
};

/**
 * Callback Playbook Modal — lazy-generates a DISC-tuned opening line,
 * 3-5 step checklist, and specific points-to-cover for a revivable deal.
 * The playbook is cached on the backend so re-opens are instant.
 *
 * Exported so agent-scoped views (MyCoachingTab) can reuse the same UX.
 */
// DISC pill styling — colour-tuned per personality style, matching the
// palette used across the Caller Intel tab so reps see a consistent
// visual language for "this is a D caller" vs "this is a C caller".
const DISC_PILL_STYLE = {
  D: { label: 'Dominance',       tone: 'direct + outcome-focused',   css: 'text-rose-800 bg-rose-100 border-rose-200' },
  I: { label: 'Influence',       tone: 'warm + social',              css: 'text-amber-800 bg-amber-100 border-amber-200' },
  S: { label: 'Steadiness',      tone: 'patient + reassuring',       css: 'text-teal-800 bg-teal-100 border-teal-200' },
  C: { label: 'Conscientious',   tone: 'structured + specific',      css: 'text-indigo-800 bg-indigo-100 border-indigo-200' },
};

export const PlaybookModal = ({ row, onClose }) => {
  const { user: authUser } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [pb, setPb] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [practiceOpen, setPracticeOpen] = React.useState(false);

  const fetchPlaybook = React.useCallback(async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setErr(null);
    try {
      const url = `${API}/analytics/revival/${row.call_id}/playbook${refresh ? '?refresh=true' : ''}`;
      const r = await axios.post(url, {}, { timeout: 90000 });
      setPb(r.data);
      if (refresh) {
        toast.success(
          r.data?.coaching_model === 'green_brain'
            ? 'Refreshed with Green Brain coaching model'
            : 'Playbook refreshed',
        );
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not generate playbook.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [row.call_id]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      // Initial load — never triggers a forced refresh. Cache is used
      // when present; the server-side auto-invalidates any pre-Green-
      // Brain cache so reps still get the empathy-first version.
      setLoading(true);
      setErr(null);
      try {
        const r = await axios.post(`${API}/analytics/revival/${row.call_id}/playbook`, {}, { timeout: 90000 });
        if (!cancelled) setPb(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || 'Could not generate playbook.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [row.call_id]);

  // Hot-refresh when a manager flips this domain's Green Brain toggle
  // in Admin. The WS payload carries `green_brain_enabled` so we can
  // show a toast that names the switch instead of a generic "refreshed".
  const wsEvent = usePlaybookSocket(pb?.domain_id);
  const lastHandledEventId = React.useRef(null);
  React.useEffect(() => {
    if (!wsEvent || !pb) return;
    if (wsEvent._id === lastHandledEventId.current) return;
    lastHandledEventId.current = wsEvent._id;
    toast.message(
      wsEvent.green_brain_enabled
        ? 'Green Brain just enabled for this tenant — refreshing your playbook…'
        : 'Green Brain was disabled for this tenant — refreshing…',
    );
    fetchPlaybook({ refresh: true });
  }, [wsEvent, pb, fetchPlaybook]);

  const copyAll = async () => {
    if (!pb) return;
    const text = [
      `Callback playbook — ${row.customer_phone || 'caller'}`,
      '',
      `OPENING LINE:`,
      `  ${pb.opening_line}`,
      '',
      `CHECKLIST:`,
      ...(pb.checklist || []).map((s, i) => `  ${i + 1}. ${s}`),
      '',
      `POINTS TO COVER:`,
      ...(pb.points_to_cover || []).map((s) => `  • ${s}`),
      '',
      `Urgency: call within ${pb.urgency_days || 3} days — ${pb.urgency_note || ''}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Playbook copied to clipboard');
    } catch { toast.error('Copy failed'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid={`playbook-modal-${row.call_id}`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-slate-900 inline-flex items-center gap-2 flex-wrap">
              <BookOpen className="w-5 h-5 text-teal-500" />
              Callback Playbook
              {pb?.coaching_model === 'green_brain' && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full"
                  title="Empathy + motivation-first coaching model (S-baseline DISC adaptation)"
                  data-testid="playbook-coaching-model-badge"
                >
                  <Sparkles className="w-3 h-3" /> Green Brain
                </span>
              )}
              {pb?.coaching_model === 'legacy_disc' && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full"
                  title="Legacy DISC-only coaching model — enable Green Brain on this domain to switch"
                  data-testid="playbook-coaching-model-badge"
                >
                  Legacy DISC
                </span>
              )}
              {pb?.disc && DISC_PILL_STYLE[pb.disc] && (
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${DISC_PILL_STYLE[pb.disc].css}`}
                  title={`Caller DISC style: ${DISC_PILL_STYLE[pb.disc].label} — ${DISC_PILL_STYLE[pb.disc].tone}`}
                  data-testid={`playbook-disc-badge-${pb.disc}`}
                >
                  {pb.disc} · {DISC_PILL_STYLE[pb.disc].label}
                </span>
              )}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5 font-mono truncate">
              {row.customer_phone || 'unknown'} · originally spoke to <span className="font-semibold">{row.original_agent_name || '—'}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 shrink-0 ml-3"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading && (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Generating playbook…
            </div>
          )}
          {err && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{err}</div>
          )}
          {pb && (
            <>
              {pb.urgency_days && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-amber-800">Call within {pb.urgency_days} days</p>
                    {pb.urgency_note && <p className="text-xs text-amber-700 mt-0.5">{pb.urgency_note}</p>}
                  </div>
                </div>
              )}

              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Opening line</p>
                <blockquote className="text-sm text-slate-800 border-l-2 border-teal-500 bg-teal-50/60 pl-3 py-2 rounded-r-lg leading-relaxed">
                  {pb.opening_line}
                </blockquote>
              </div>

              {pb.checklist?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Checklist</p>
                  <ol className="space-y-1.5 text-sm text-slate-700">
                    {pb.checklist.map((s, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-teal-500 text-white text-[11px] font-bold shrink-0">{i + 1}</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {pb.points_to_cover?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Points to cover</p>
                  <ul className="space-y-1 text-sm text-slate-700">
                    {pb.points_to_cover.map((s, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-teal-500 mt-1 shrink-0" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {pb.close_probability != null && (
                <div className="text-xs text-slate-500">
                  Estimated close probability if you follow this playbook:{' '}
                  <span className="font-bold text-slate-800 tabular-nums">
                    {Math.round(pb.close_probability * 100)}%
                  </span>
                  {pb.cached && <span className="ml-2 text-[10px] text-slate-400 italic">(cached)</span>}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          {pb?.pre_call_sms && canSendSms(authUser) && (
            <div className="border-t border-slate-200 pt-3 mb-2" data-testid="playbook-modal-sms-section">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Send SMS (AI draft — edit before sending)</p>
              <PreCallSmsSendButtons
                callId={row.call_id}
                content={pb.pre_call_sms}
                gymsales={pb.sources?.gymsales}
              />
            </div>
          )}
          <p className="text-[11px] text-slate-500 italic">AI-generated from the original call. Adapt the language to sound like you.</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchPlaybook({ refresh: true })}
              disabled={loading || refreshing}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 disabled:opacity-50"
              data-testid="playbook-refresh"
              title="Regenerate this playbook with the freshest coaching model (skips cache)"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing…' : 'Refresh playbook'}
            </button>
            <button
              onClick={() => setPracticeOpen(true)}
              disabled={!pb}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-indigo-300 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold disabled:opacity-50"
              data-testid="playbook-practice"
              title="Rehearse this callback against an AI playing the customer"
            >
              <Sparkles className="w-3 h-3" /> Practice
            </button>
            <button
              onClick={copyAll}
              disabled={!pb}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 disabled:opacity-50"
              data-testid="playbook-copy"
            >
              <Copy className="w-3 h-3" /> Copy playbook
            </button>
            <button
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded-lg bg-teal-500 hover:bg-teal-400 text-white font-semibold"
            >Close</button>
          </div>
        </div>
      </div>
      {practiceOpen && (
        <PracticeModal callId={row.call_id} openingLine={pb?.opening_line} onClose={() => setPracticeOpen(false)} />
      )}
    </div>
  );
};


/**
 * Re-allocate Modal — manager picks a different agent to own a stalled
 * opportunity, with an optional note that lands in the audit trail.
 */
export const ReassignModal = ({ row, domainId, onClose, onDone }) => {
  const [peers, setPeers] = useState([]);
  const [to, setTo] = useState(row?.__prefill_agent?.agent_id || '');
  const [note, setNote] = useState(row?.__prefill_agent ? `Assigned via top-3 recommendation` : '');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const q = domainId ? `?domain_id=${domainId}` : '';
        const r = await axios.get(`${API}/gamification/peers${q}`);
        if (!cancelled) setPeers(r.data?.peers || []);
      } catch {
        if (!cancelled) setPeers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [domainId]);

  const submit = async () => {
    if (!to) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/analytics/revival/${row.call_id}/reassign`,
        { new_agent_id: to, note: note || null },
      );
      onDone?.({ agent_id: r.data.new_agent_id, agent_name: r.data.new_agent_name });
      if (r.data.matched_recommendation_rank === 1) {
        toast.success('Matched the #1 engine recommendation — nice call.');
      } else if (r.data.matched_recommendation_rank) {
        toast.success(`Matched engine Top-${r.data.matched_recommendation_rank}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not re-allocate');
    } finally { setBusy(false); }
  };

  const currentOwner = row.suggested_agent_name || row.original_agent_name || '—';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="reassign-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white px-5 py-4 flex items-start justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-100">
              Re-allocate opportunity
            </p>
            <p className="text-sm font-bold mt-0.5">{row.customer_phone || '—'}</p>
            <p className="text-[11px] text-teal-100 mt-0.5">
              currently owned by <span className="font-semibold text-white">{currentOwner}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/70 hover:text-white"
            data-testid="reassign-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">
              Re-allocate to
            </label>
            {loading ? (
              <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
                <Loader2 className="w-3 h-3 animate-spin" /> loading peers…
              </div>
            ) : (
              <select
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                data-testid="reassign-peer-select"
              >
                <option value="">— pick an agent —</option>
                {peers.map((p) => (
                  <option key={p.agent_id} value={p.agent_id}>{p.agent_name}</option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">
              Note (optional)
            </label>
            <textarea
              className="w-full text-sm border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none"
              rows={3}
              placeholder='e.g. "Better rapport with C-type callers"'
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={280}
              data-testid="reassign-note"
            />
            <p className="text-[10px] text-slate-400 mt-1">{note.length}/280 · saved to audit trail</p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="text-xs font-semibold text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || !to}
              className="text-xs font-semibold bg-teal-600 text-white px-3 py-1.5 rounded-lg hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="reassign-submit"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
              Confirm re-allocation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Best-Agent Recommendation Modal — shows the top-3 agents the historical
 * matrix predicts are most likely to close this revivable deal. Clicking a
 * candidate hands off to the existing ReassignModal.
 */
const RecommendAgentsModal = ({ row, onClose, onPick }) => {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const r = await axios.get(
          `${API}/analytics/revival/${row.call_id}/recommend-agents?top_n=3`,
          { timeout: 60000 },
        );
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || 'Could not load recommendations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [row.call_id]);

  const recs = data?.recommendations || [];
  const emptyReason = data?.reason;
  const disc = data?.caller_disc;

  const emptyMessage = {
    'no-disc-profile': 'This caller has no DISC profile yet — the engine needs at least one graded call to profile them.',
    'no-historical-data': 'No historical outcomes for this DISC-type caller in this domain yet. Come back after more calls are graded.',
    'no-in-site-candidates': `No agents on ${data?.original_site || 'this site'} have handled enough ${data?.caller_disc || ''}-type callers yet. Reassigning across sites requires a manual re-allocation.`,
    'call-not-found': 'Call not found.',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="recommend-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white px-5 py-4 flex items-start justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-100 inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Best-agent recommendation
            </p>
            <p className="text-sm font-bold mt-0.5">{row.customer_phone || '—'}</p>
            <p className="text-[11px] text-indigo-100 mt-0.5">
              {disc && (
                <>
                  Caller DISC: <span className="font-semibold text-white">{disc}</span>
                  {data?.original_site && (
                    <span className="ml-2">
                      · Site: <span className="font-semibold text-white">{data.original_site}</span>
                    </span>
                  )}
                  {data?.team_base_rate != null && (
                    <span className="ml-2">
                      · Team avg: <span className="font-semibold text-white">
                        {Math.round(data.team_base_rate * 100)}%
                      </span>
                    </span>
                  )}
                </>
              )}
              {!disc && !loading && 'Historical performance • same-site agents'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/70 hover:text-white"
            data-testid="recommend-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {loading && (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Analysing historical data…
            </div>
          )}
          {err && !loading && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-700">
              {err}
            </div>
          )}
          {!loading && !err && recs.length === 0 && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 text-center">
              {emptyMessage[emptyReason] || 'No recommendations available right now.'}
            </div>
          )}
          {!loading && recs.length > 0 && (
            <ul className="space-y-2.5" data-testid="recommend-list">
              {recs.map((r, i) => (
                <li
                  key={r.agent_id}
                  className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
                  data-testid={`recommend-item-${r.agent_id}`}
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                    style={{ background: i === 0 ? '#4F46E5' : i === 1 ? '#7C3AED' : '#94A3B8' }}
                  >
                    #{i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {r.agent_name}
                      </p>
                      {r.is_original_agent && (
                        <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
                          Original
                        </span>
                      )}
                      {Number(r.chase_boost || 0) > 0 && (
                        <span
                          className="text-[9px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 rounded px-1.5 py-0.5 inline-flex items-center gap-1"
                          title={`Proven chase performer for ${data?.caller_disc || 'this'} callers`}
                          data-testid={`recommend-chase-badge-${r.agent_id}`}
                        >
                          Proven chaser · +{r.chase_boost}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.min(100, r.effective_confidence ?? r.confidence)}%`,
                            background:
                              (r.effective_confidence ?? r.confidence) >= 60 ? '#10B981'
                              : (r.effective_confidence ?? r.confidence) >= 35 ? '#F59E0B'
                              : '#EF4444',
                          }}
                        />
                      </div>
                      <span className="text-[11px] font-bold tabular-nums text-slate-700 w-8 text-right">
                        {r.effective_confidence ?? r.confidence}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1 leading-snug">{r.reasoning}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onPick?.({ agent_id: r.agent_id, agent_name: r.agent_name })}
                    className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white inline-flex items-center gap-1 shrink-0"
                    data-testid={`recommend-pick-${r.agent_id}`}
                  >
                    Assign <ArrowRight className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-slate-400 mt-4 leading-snug italic">
            Confidence blends smoothed win rate × sample-size confidence over the past {data?.window_days || 180} days.
            Agents with fewer than {data?.min_samples_for_candidate || 3} matching-DISC deals only appear as fallbacks.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RevivableDealQueue;